// src/features/records/components/GalleryView/components/MediaLightbox/components/VideoPlayer.js
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Loader2, VideoOff } from 'lucide-react';
import styles from './VideoPlayer.module.css';

/**
 * VideoPlayer - Displays video with native HTML5 controls
 * Auto-pauses on navigation and visibility changes
 */
const VideoPlayer = ({ 
  item, 
  url, 
  posterUrl,
  isActive,
  isPaused,
  onPlay,
  onPause,
  onError 
}) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasUserPlayed, setHasUserPlayed] = useState(false);
  const bufferingTimerRef = useRef(null);
  
  // Reset UI when source changes; force load to reset pipeline
  useEffect(() => {
    setError(false);
    setLoading(false);
    setHasUserPlayed(false);
    clearTimeout(bufferingTimerRef.current);
    if (videoRef.current && url) {
      // Ensure the element restarts its load sequence on src change
      videoRef.current.load();
    }
  }, [url]);
  
  // Symmetric external control
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !url) return;
    
    if (!isActive || isPaused) {
      // Pause deterministically
      if (!v.paused) v.pause();
      // Don't show spinner when becoming inactive/paused
      setLoading(false);
      return;
    }
    
    // Try to play when active & unpaused (keyboard toggle etc.)
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay restrictions or other transient errors – ignore quietly
      });
    }
  }, [isActive, isPaused, url]);
  
  // Pause on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause(); // pause event will trigger handlePause
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
      }
    };
  }, []);
  
  // Debounced buffering indicator
  const showBuffering = () => {
    clearTimeout(bufferingTimerRef.current);
    bufferingTimerRef.current = setTimeout(() => {
      setLoading(true);
    }, 150);
  };
  
  const hideBuffering = () => {
    clearTimeout(bufferingTimerRef.current);
    setLoading(false);
  };
  
  // Event handlers (React bindings avoid manual add/remove)
  const handleLoadedMetadata = () => {
    setError(false);
    setLoading(false);
  };
  
  const handleCanPlay = hideBuffering;
  const handlePlaying = hideBuffering;
  
  const handleWaiting = () => {
    if (hasUserPlayed) showBuffering();
  };
  
  const handleStalled = () => {
    if (hasUserPlayed) showBuffering();
  };
  
  const handleEnded = hideBuffering;
  const handleSuspend = hideBuffering;
  
  const handleError = (e) => {
    console.error('Video error:', e);
    clearTimeout(bufferingTimerRef.current);
    setError(true);
    setLoading(false);
    onError?.();
  };
  
  const handlePlay = () => {
    setHasUserPlayed(true);
    const v = videoRef.current;
    if (v && v.readyState < 2) {
      showBuffering();
    }
    onPlay?.();
  };
  
  const handlePause = () => {
    hideBuffering();
    onPause?.();
  };
  
  return (
    <div className={styles.container} aria-busy={loading}>
      {/* Loading state - non-blocking with pointer-events: none */}
      {loading && (
        <div className={styles.loading} style={{ pointerEvents: 'none' }}>
          <Loader2 className={styles.spinner} size={48} />
        </div>
      )}
      
      {/* Error state */}
      {error ? (
        <div className={styles.error} role="alert" aria-live="polite">
          <VideoOff size={48} />
          <p>{t('records.views.gallery.errors.video_load_failed')}</p>
        </div>
      ) : (
        /* Video element - only render if no error */
        url && (
          <video
            ref={videoRef}
            className={styles.video}
            src={url}
            poster={posterUrl}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
            onCanPlay={handleCanPlay}
            onPlaying={handlePlaying}
            onWaiting={handleWaiting}
            onStalled={handleStalled}
            onEnded={handleEnded}
            onSuspend={handleSuspend}
            onError={handleError}
            onPlay={handlePlay}
            onPause={handlePause}
            // Optional: uncomment for seeking feedback
            // onSeeking={() => hasUserPlayed && showBuffering()}
            // onSeeked={hideBuffering}
          />
        )
      )}
    </div>
  );
};

VideoPlayer.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    duration: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number
  }).isRequired,
  url: PropTypes.string,
  posterUrl: PropTypes.string,
  isActive: PropTypes.bool,
  isPaused: PropTypes.bool,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onError: PropTypes.func
};

VideoPlayer.defaultProps = {
  isActive: true,
  isPaused: true
};

export default VideoPlayer;
